import React, { useCallback, useMemo } from 'react';

import DropdownView from '@/components/quick-replies/dropdown-view';
import { VariableItem } from '@/components/rich-text-editor/types/rich-text-editor';

import ChatMessageInput from '@/shared/components/chat-message-input/chat-message-input';
import { COPY } from '@/shared/constants/copies';
import {
	ASSIGNMENT_LINK_VARIABLE,
	offersAssignmentLink,
} from '@/shared/constants/message-constants';
import { useMessageVariableValues } from '@/shared/hooks/use-message-variables';
import { IQuickReply } from '@/shared/services/forms/forms.types';
import { hasMessageVariable } from '@/shared/utils/message-variables';

import { cn } from '@/lib/utils';

/** This panel owns the box (border, background, focus ring), so the editor draws none of its own. */
const RTE_CHROME_RESET = 'focus-within:border-transparent focus-within:outline-none focus-within:ring-0';

export type MessageComposerPanelProps = {
	value: string;
	onChange: (html: string) => void;
	/** Quick replies offered under the editor. Pass `[]` for none. */
	quickReplies?: IQuickReply[];
	/** Bulk sends keep `{first_name}` for the backend; single sends resolve it in the quick reply. */
	isBulkAction: boolean;
	onFocus?: () => void;
	onBlur?: () => void;
	placeholder?: string;
	disabled?: boolean;
	isProcessing?: boolean;
	error?: string | null;
	variables?: VariableItem[];
	getVariableTooltip?: (variable: VariableItem) => string;
	formId?: string;
	firstName?: string;
	jobName?: string;
	onUpdateQuickReplies?: (quickReplies: IQuickReply[]) => void;
	className?: string;
};

function MessageComposerPanel({
	value,
	onChange,
	quickReplies = [],
	isBulkAction,
	onFocus,
	onBlur,
	placeholder = 'Write your message here...',
	disabled = false,
	isProcessing = false,
	error = null,
	variables,
	getVariableTooltip,
	formId,
	firstName = '',
	jobName,
	onUpdateQuickReplies,
	className,
}: MessageComposerPanelProps) {
	const handleQuickReplySelect = useCallback(
		(quickReplyFormattedText: string) => {
			if (isProcessing) return;
			onChange(quickReplyFormattedText);
		},
		[isProcessing, onChange]
	);

	const variableValues = useMessageVariableValues({ firstName, jobName });

	// Derived from the editor's own variable list, so one prop drives the pill menu and both hints.
	const supportsAssignmentLink = useMemo(() => offersAssignmentLink(variables), [variables]);

	const showsUnsupportedLinkNote =
		!supportsAssignmentLink && hasMessageVariable(value, ASSIGNMENT_LINK_VARIABLE.id);

	return (
		<>
			<div
				className={cn(
					'relative rounded-md border border-border bg-white',
					'focus-within:border-primary focus-within:ring-1 focus-within:ring-primary',
					disabled && 'bg-muted/30',
					className
				)}
			>
				{error && (
					<p className="absolute bottom-full right-0 truncate pb-1 text-xs font-semibold text-red-500">
						{error}
					</p>
				)}

				<ChatMessageInput
					value={value}
					onChange={onChange}
					onFocus={onFocus}
					onBlur={onBlur}
					placeholder={placeholder}
					disabled={disabled}
					enableVariableList
					variables={variables}
					getVariableTooltip={getVariableTooltip}
					className="w-full"
					// Bottom padding is the only room reserved for the floating action, so text spans full width.
					contentClassName={cn(
						'min-h-[120px] pb-9 text-sm leading-normal',
						disabled && 'text-muted-foreground/80'
					)}
					rteClassName={cn('max-h-[220px] border-none bg-transparent p-3', RTE_CHROME_RESET)}
				/>

				<div className="absolute bottom-2 right-2">
					<DropdownView
						quickReplies={quickReplies}
						variableValues={variableValues}
						onSelect={handleQuickReplySelect}
						overrideCmdEnter
						formId={formId}
						onUpdateQuickReplies={onUpdateQuickReplies}
						isBulkAction={isBulkAction}
						supportsAssignmentLink={supportsAssignmentLink}
					/>
				</div>

				{isProcessing && <div className="absolute inset-0 rounded-md bg-white/60" />}
			</div>

			{showsUnsupportedLinkNote && (
				<p className="text-[13px] text-secondary">
					{COPY.messaging.assignmentLinkUnsupported(ASSIGNMENT_LINK_VARIABLE.token)}
				</p>
			)}
		</>
	);
}

export default React.memo(MessageComposerPanel);
